import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useMemo, useState } from "react";
import { AUTHORITY_GROUP_ORDER, authorityGroup, authorityLabel, roleLabel } from "../../lib/labels";
import { usePageTitle } from "../../app/usePageTitle";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { useAuthorities, useCreateRole, useDeleteRole, useRoleList, useSaveRole } from "./api";
import type { Role } from "../../types/user";
import { Authority } from "../../auth/authorities";
import { useAuth } from "../../auth/useAuth";
import { useNotify } from "../../app/useNotify";
import { errorMessageFor } from "../../api/client";

export function RolesPage() {
  usePageTitle("Roles");
  const { can, principal } = useAuth();
  const notify = useNotify();
  const authorities = useAuthorities();
  const roles = useRoleList();
  const save = useSaveRole();
  const create = useCreateRole();
  const remove = useDeleteRole();

  const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null);
  const [deleting, setDeleting] = useState<Role | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const columns = useMemo(
    () => (authorities.data ?? []).filter((authority) => authority.visible),
    [authorities.data],
  );

  /** Grouped by the area each permission governs, rather than database order. */
  const groups = useMemo(() => {
    const byGroup = new Map<string, typeof columns>();
    for (const authority of columns) {
      const group = authorityGroup(authority.name);
      byGroup.set(group, [...(byGroup.get(group) ?? []), authority]);
    }
    return AUTHORITY_GROUP_ORDER.filter((group) => byGroup.has(group)).map((group) => ({
      group,
      authorities: byGroup.get(group)!,
    }));
  }, [columns]);

  /** Pending permission changes, keyed by role id. Nothing is sent until Save. */
  const [pending, setPending] = useState<Record<number, Set<number>>>({});
  const dirtyRoleIds = Object.keys(pending).map(Number);
  const dirty = dirtyRoleIds.length > 0;

  function grantedFor(role: Role): Set<number> {
    return pending[role.id] ?? new Set(role.authorities.map((authority) => authority.id));
  }

  function stage(role: Role, authorityId: number, granted: boolean) {
    const next = new Set(grantedFor(role));
    if (granted) next.add(authorityId);
    else next.delete(authorityId);
    setPending((previous) => ({ ...previous, [role.id]: next }));
  }

  async function saveAll() {
    const roleById = new Map((roles.data ?? []).map((role) => [role.id, role]));
    try {
      for (const roleId of dirtyRoleIds) {
        const role = roleById.get(roleId);
        if (!role) continue;
        await save.mutateAsync(payloadFor(role, [...pending[roleId]]));
      }
      setPending({});
      notify.success(dirtyRoleIds.length === 1 ? "Permissions saved" : "Permissions saved");
    } catch (error) {
      notify.error(errorMessageFor(error) ?? "Could not save the permissions.");
    }
  }

  /** A role the signed-in user holds cannot be edited from here. */
  function editable(role: Role): boolean {
    return (
      role.canBeModified && !(principal?.roles ?? []).includes(role.name) && can(Authority.EditRole)
    );
  }

  function payloadFor(role: Role, authorityIds: number[]) {
    return { id: role.id, name: role.name, authorities: authorityIds.map((id) => ({ id })) };
  }

  async function commitRename(role: Role, name: string) {
    try {
      await save.mutateAsync({
        ...payloadFor(
          role,
          role.authorities.map((authority) => authority.id),
        ),
        name,
      });
      notify.success("Role renamed");
      setRenaming(null);
    } catch (error) {
      notify.error(errorMessageFor(error) ?? "Unable to change the role name.");
    }
  }

  if (authorities.isLoading || roles.isLoading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}
      >
        {can(Authority.CreateRole) && (
          <Button variant="contained" onClick={() => setCreating(true)}>
            Add role
          </Button>
        )}
      </Stack>

      {dirty && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" sx={{ gap: 1 }}>
              <Button size="small" onClick={() => setPending({})} disabled={save.isPending}>
                Discard
              </Button>
              <Button size="small" variant="contained" onClick={saveAll} disabled={save.isPending}>
                Save changes
              </Button>
            </Stack>
          }
        >
          {dirtyRoleIds.length === 1
            ? "You have unsaved permission changes to 1 role."
            : `You have unsaved permission changes to ${dirtyRoleIds.length} roles.`}
        </Alert>
      )}

      <Paper>
        <TableContainer sx={{ overflowX: "auto", maxHeight: "70vh" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ position: "sticky", left: 0, zIndex: 3, bgcolor: "background.paper" }}
                >
                  Action
                </TableCell>
                <TableCell
                  sx={{ position: "sticky", left: 80, zIndex: 3, bgcolor: "background.paper" }}
                >
                  Role Name
                </TableCell>
                {groups.flatMap(({ group, authorities: inGroup }) =>
                  inGroup.map((authority, index) => (
                    <TableCell
                      key={authority.id}
                      align="center"
                      sx={{
                        whiteSpace: "nowrap",
                        ...(index === 0 ? { borderLeft: 2, borderColor: "divider" } : {}),
                      }}
                    >
                      <Box sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600 }}>
                        {index === 0 ? group.toUpperCase() : "\u00a0"}
                      </Box>
                      {authorityLabel(authority.name)}
                    </TableCell>
                  )),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {(roles.data ?? []).map((role) => {
                const granted = grantedFor(role);
                const canEdit = editable(role);
                const isRenaming = renaming?.id === role.id;
                return (
                  <TableRow key={role.id} hover>
                    <TableCell
                      sx={{ position: "sticky", left: 0, zIndex: 2, bgcolor: "background.paper" }}
                    >
                      <Stack direction="row">
                        {isRenaming ? (
                          <>
                            <Tooltip title="Save name">
                              <IconButton
                                size="small"
                                aria-label={`Save name for ${roleLabel(role.name)}`}
                                onClick={() => commitRename(role, renaming.name)}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              aria-label="Cancel rename"
                              onClick={() => setRenaming(null)}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          canEdit && (
                            <Tooltip title="Rename">
                              <IconButton
                                size="small"
                                aria-label={`Rename ${roleLabel(role.name)}`}
                                onClick={() => setRenaming({ id: role.id, name: role.name })}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                        {can(Authority.DeleteRole) && role.canBeModified && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              aria-label={`Delete ${roleLabel(role.name)}`}
                              onClick={() => setDeleting(role)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 80,
                        zIndex: 2,
                        bgcolor: "background.paper",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {isRenaming ? (
                        <TextField
                          size="small"
                          value={renaming.name}
                          autoFocus
                          slotProps={{ htmlInput: { "aria-label": "Role name" } }}
                          onChange={(event) =>
                            setRenaming({ id: role.id, name: event.target.value })
                          }
                        />
                      ) : (
                        roleLabel(role.name)
                      )}
                    </TableCell>
                    {groups.flatMap(({ authorities: inGroup }) =>
                      inGroup.map((authority, index) => (
                        <TableCell
                          key={authority.id}
                          align="center"
                          sx={index === 0 ? { borderLeft: 2, borderColor: "divider" } : undefined}
                        >
                          <Checkbox
                            size="small"
                            checked={granted.has(authority.id)}
                            disabled={!canEdit || save.isPending}
                            slotProps={{
                              input: {
                                "aria-label": `${authorityLabel(authority.name)} for ${roleLabel(role.name)}`,
                              },
                            }}
                            onChange={(event) => stage(role, authority.id, event.target.checked)}
                          />
                        </TableCell>
                      )),
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <ConfirmDialog
        open={creating}
        title="Add role"
        confirmLabel="Create"
        busy={create.isPending}
        confirmDisabled={newName.trim() === ""}
        onClose={() => {
          setCreating(false);
          setNewName("");
        }}
        onConfirm={async () => {
          try {
            await create.mutateAsync(newName.trim());
            notify.success("Role created");
            setCreating(false);
            setNewName("");
          } catch (error) {
            notify.error(errorMessageFor(error) ?? "Unable to create the role.");
          }
        }}
      >
        <TextField
          label="Role name"
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          fullWidth
          margin="normal"
          autoFocus
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete the role ${roleLabel(deleting?.name ?? "")}?`}
        message="Anyone holding it loses those permissions. This cannot be undone."
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync(deleting.id);
            notify.success("Role deleted");
          } catch (error) {
            notify.error(errorMessageFor(error) ?? "Unable to delete the role.");
          } finally {
            setDeleting(null);
          }
        }}
      />
    </Box>
  );
}
